import {useEffect, useRef, useState} from 'react';

import type {EagGridStore} from '../store/createEagGridStore';
import type {GetDataQueryResponse} from '../types/api/data';
import type {Filters} from '../types/Filters';
import type {QueryFilterObject} from '../types/QueryFilterObject';
import {buildFilterValuesFromDataQuery} from '../utils/buildFilterValuesFromDataQuery';
import {isOnlyOrderChanged} from '../utils/isOnlyOrderChanged';

export type UseFilterHydrationParams = {
  dataQuery: GetDataQueryResponse | null | undefined;
  queryModifier: ((filter: QueryFilterObject) => QueryFilterObject) | undefined;
  initialSmartSearch: string | undefined;
  store: Pick<EagGridStore, 'setStaticFilters' | 'setFilterValues'>;
};

/**
 * Watches `dataQuery` and re-seeds `filterValues` in the store whenever the
 * server delivers a new data query (initial load, preset switch, query-id fallback).
 *
 * Two guards prevent spurious re-seeding:
 *   Guard 1 — exact same object reference: no-op (React double-invoke safety).
 *   Guard 2 — only `order` changed (`setColumnSort` preserves `.filters` reference):
 *             skips `setFilterValues` to avoid a duplicate `createDataQuery` POST.
 *
 * Returns `isFilterHydrated` — becomes `true` after the first successful seed.
 */
export function useFilterHydration(params: UseFilterHydrationParams): boolean {
  const [isFilterHydrated, setIsFilterHydrated] = useState(false);
  const prevDataQueryRef = useRef<GetDataQueryResponse | null>(null);

  // eslint-disable-next-line eslint-js/no-restricted-syntax -- intentional: reactive sync; re-seeds filter values in store whenever dataQuery changes (initial load + every preset switch); prev-ref guard prevents double-run
  useEffect(
    function hydrateFiltersFromDataQuery() {
      // Static filters must be recomputed whenever queryModifier changes, even when
      // dataQuery hasn't changed (Guard 1 would skip it). Without this, a late-arriving
      // queryModifier leaves staticFilters as {} → the ActiveFilterBar shows static
      // filter chips ("second DG load" bug, T20-132568).
      const staticFilters = params.queryModifier ? params.queryModifier({}) : {};
      params.store.setStaticFilters(staticFilters);

      if (!params.dataQuery) {
        return;
      }

      const prevDataQuery = prevDataQueryRef.current;

      // Guard 1: exact same object reference — no change at all.
      if (prevDataQuery === params.dataQuery) {
        return;
      }

      prevDataQueryRef.current = params.dataQuery;

      // Guard 2: only dataQuery.order changed (setColumnSort spreads state.dataQuery
      // preserving the .filters reference). Re-initializing filterValues here would
      // trigger a spurious fetchPage → duplicate data-query POST in the network.
      if (isOnlyOrderChanged(prevDataQuery, params.dataQuery)) {
        return;
      }

      // `initialSmartSearch` is an optional consumer override (e.g. a deep-link search term);
      // buildFilterValuesFromDataQuery treats an empty string as "not provided" — consumers
      // commonly pass `someValue ?? ''`, and a blank '' must not shadow the smart-search value
      // persisted in the preset's dataQuery (which would drop the saved search after reload).
      // The util also normalizes both BE filter formats (flat and wrapped).
      const newInitialFilters: Filters = buildFilterValuesFromDataQuery(
        params.dataQuery,
        params.initialSmartSearch
      );

      params.store.setFilterValues(newInitialFilters);
      setIsFilterHydrated(true);
    },
    [params.dataQuery, params.queryModifier, params.initialSmartSearch, params.store] // eslint-disable-line react/exhaustive-deps -- Muted pre-existing violation
  );

  return isFilterHydrated;
}
